import type { QueryClient } from "@tanstack/react-query";

import type { ServerEvent } from "@/api/types";

export interface EventConnection {
  close: () => void;
}

export function connectServerEvents(queryClient: QueryClient, onState?: (connected: boolean) => void): EventConnection {
  let source: EventSource | null = null;
  let reconnectTimer: number | null = null;
  let stopped = false;
  let retry = 0;
  let lastEventId = "";

  const invalidate = (event: ServerEvent): void => {
    if (event.type.startsWith("paper.") || event.type.startsWith("artifact.") || event.type.startsWith("audio.")) {
      void queryClient.invalidateQueries({ queryKey: ["papers"] });
      if (event.paper_id) void queryClient.invalidateQueries({ queryKey: ["paper", event.paper_id] });
    }
    if (event.type.startsWith("job.")) void queryClient.invalidateQueries({ queryKey: ["job"] });
    if (event.type.startsWith("sync.")) {
      void queryClient.invalidateQueries({ queryKey: ["sync"] });
      const resource = (event as ServerEvent & { resource?: string }).resource;
      if (resource === "settings" || resource === "fetch_groups" || resource === "workspace_bootstrap") {
        void queryClient.invalidateQueries({ queryKey: ["settings"] });
        void queryClient.invalidateQueries({ queryKey: ["workspace-settings"] });
      }
    }
    if (event.type.startsWith("admin.")) void queryClient.invalidateQueries({ queryKey: ["admin"] });
  };

  const open = (): void => {
    reconnectTimer = null;
    if (stopped || document.visibilityState === "hidden") return;
    const cursor = lastEventId ? `?last_event_id=${encodeURIComponent(lastEventId)}` : "";
    source = new EventSource(`/api/events${cursor}`, { withCredentials: true });
    source.onopen = () => { retry = 0; onState?.(true); };
    const handle = (type: "paper.changed" | "job.changed" | "sync.changed") => (raw: Event): void => {
      const message = raw as MessageEvent<string>;
      if (message.lastEventId) lastEventId = message.lastEventId;
      try {
        const payload = JSON.parse(message.data) as Omit<ServerEvent, "type">;
        invalidate({ ...payload, type } as ServerEvent);
      } catch { /* Ignore malformed events and keep the stream alive. */ }
    };
    source.addEventListener("paper.changed", handle("paper.changed"));
    source.addEventListener("job.changed", handle("job.changed"));
    source.addEventListener("sync.changed", handle("sync.changed"));
    source.onerror = () => {
      onState?.(false);
      source?.close();
      source = null;
      if (stopped) return;
      const delay = Math.min(60_000, 1_000 * (2 ** retry));
      retry = Math.min(retry + 1, 6);
      reconnectTimer = window.setTimeout(() => {
        if (delay >= 60_000) {
          void queryClient.invalidateQueries({ queryKey: ["auth", "check"] });
        }
        open();
      }, delay);
    };
  };

  const visibility = (): void => {
    if (document.visibilityState === "hidden") {
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
      source?.close();
      source = null;
      onState?.(false);
    } else if (!source && reconnectTimer === null) {
      open();
    }
  };

  document.addEventListener("visibilitychange", visibility);
  open();

  return {
    close: () => {
      stopped = true;
      document.removeEventListener("visibilitychange", visibility);
      source?.close();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
    },
  };
}
